import * as z from "zod";
import { userFacingError } from "../authorization";
import {
  customDomainHostname,
  customDomainSchema,
  DOMAIN_MAX_LENGTH,
  countsTowardDomainLimit,
} from "../customDomains";
import { zql } from "../generatedDrizzleSchema";
import { MAX_CUSTOM_DOMAINS_PER_TENANT } from "../stackLimits";
import { assertSpaceDomain, domainConfig } from "./customDomains";
import { mutator } from "./mutator";

export const addCustomDomain = mutator({
  ...domainConfig,
  description: "Sets up a custom domain for public workflow URLs.",
  interactionType: "create",
  schema: domainConfig.schema.extend({
    domain: customDomainSchema,
    spaceId: z.string().nullable().optional(),
  }),
  resolve: { space: { kind: "space", from: "spaceId", optional: true } },
  fn: async ({ tx, ctx, args: { id, domain, space } }) => {
    if (space?.mode === "personal")
      throw userFacingError("Choose a non-personal space.");
    assertSpaceDomain(domain, space?.id);
    const hostname = customDomainHostname(domain, space?.id);
    if (hostname.length > DOMAIN_MAX_LENGTH)
      throw userFacingError(`Maximum ${DOMAIN_MAX_LENGTH} characters.`);
    const existing = await tx.run(
      zql.customDomainSetups.where("tenantId", ctx.tenantId)
    );
    if (existing.some((setup) => setup.hostname === hostname))
      throw userFacingError("This domain already has a domain setup.");
    if (
      existing.filter(countsTowardDomainLimit).length >=
      MAX_CUSTOM_DOMAINS_PER_TENANT
    )
      throw userFacingError(
        `A tenant can have at most ${MAX_CUSTOM_DOMAINS_PER_TENANT} custom domains.`
      );
    const now = Date.now();
    await tx.mutate.customDomainSetups.insert({
      id,
      domain,
      hostname,
      spaceId: space?.id ?? null,
      enabled: false,
      preferredAt: null,
      managed: true,
      status: "pending",
      dnsRecords: [],
      ownershipToken: null,
      lastError: null,
      lastCheckedAt: null,
      activatedAt: null,
      createdAt: now,
      updatedAt: now,
      tenantId: ctx.tenantId,
    });
  },
});
